import { useState } from "react";
import { Drawer } from "@mui/material";
import CustomIcon from "../../components/CustomIcon";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { useTranslation } from "react-i18next";
import type { VideoPublisherSource } from "../../domains/content/videoPublisher";
import {
  doiUrl,
  externalSourceUrl,
} from "../../domains/article/utils/sourceUrl";
import { z } from "zod";

type VideoStoryDrawerProps = {
  story: VideoPublisherSource;
};

const publicationArticleSchema = z.object({
  doi: z.string().nullable(),
  externalLink: z.string().nullable(),
});
const videoStoryPublicationSchema = z.object({
  associatedArticles: z.array(publicationArticleSchema),
});

function publicationUrl(
  article: z.infer<typeof publicationArticleSchema>
): string | null {
  const external =
    article.externalLink === null
      ? null
      : externalSourceUrl(article.externalLink);
  if (external !== null) return external;
  return article.doi === null ? null : doiUrl(article.doi);
}

function publicationUrlForStory(story: VideoPublisherSource) {
  const storyResult = videoStoryPublicationSchema.safeParse(story);
  if (!storyResult.success) {
    throw new Error("Invariant: Video story publication data is invalid");
  }

  const associatedArticle = storyResult.data.associatedArticles.at(0);
  if (associatedArticle === undefined) return null;

  return publicationUrl(associatedArticle);
}

export default function VideoStoryDrawer({ story }: VideoStoryDrawerProps) {
  const { t } = useTranslation();
  const { isRtl } = useActiveLang();
  const [isOpen, setIsOpen] = useState(false);

  const sourceUrl = publicationUrlForStory(story);

  return (
    <>
      <CustomIcon
        scale={0.8}
        iconName="info"
        onClick={() => {
          setIsOpen(!isOpen);
        }}
      />
      <Drawer
        anchor={isRtl ? "left" : "right"}
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
        sx={{
          "& .MuiDrawer-paper": {
            width: "100%",
          },
        }}
      >
        <div className="p-5 flex flex-col gap-6 bg-secondary-soft-yellow-grey min-h-full pt-[calc(16px+var(--safe-area-top))]">
          <div className="flex items-center justify-end">
            <CustomIcon
              iconName="close_alt"
              onClick={() => {
                setIsOpen(false);
              }}
            />
          </div>
          <div className="mt-4">
            <div className="text-base font-bold text-secondary-bluegrey-5">
              {t("Content Liability")}
            </div>
            <div className="text-base/tight  text-secondary-bluegrey-5">
              {t(
                "This content is a summary (potentially AI-assisted) of the original source article, provided for informational purposes. It may contain simplifications or errors. For any clinical or research application, please refer to the complete source document, available below."
              )}
            </div>
          </div>
          {sourceUrl !== null ? (
            <a
              className="block bg-white py-8 px-4 rounded-2xl no-underline text-primary-black"
              href={sourceUrl}
              target="_blank"
              rel="noreferrer"
            >
              <div className="flex justify-between items-center">
                <div className="space-y-2 max-w-52">
                  <div className="font-bold leading-none">
                    {t("More information about this publication")}
                  </div>
                  <div className="text-sm leading-none">
                    {t("Take me to the full original publication")}
                  </div>
                </div>
                <div>
                  <CustomIcon iconName="editorial" />
                </div>
              </div>
            </a>
          ) : undefined}
        </div>
      </Drawer>
    </>
  );
}
